import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import {
  ProductDescription,
  ProductFeatures,
} from '@hotelplan/components.common.product';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import PdpPageContainer from 'components/domain/pdp/PdpPageContainer';
import { PdpStructuredData } from 'components/domain/structured-data';
import { useGetPdpDescriptionQuery } from 'graphql/pdp/GetPdpDescription.generated';
import PdpDescriptionSkeleton from './PdpDescriptionSkeleton.skeleton';

const DescriptionPageContent = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    flexDirection: ['column', 'row'],
    gap: [null, 4],
    '.lead-text': {
      mb: 3,
    },
  })
);

const DescriptionPageMain = styled.div(
  sx2CssThemeFn({
    flex: [null, '1'],
  })
);

const DescriptionPageTitle = styled.div(
  sx2CssThemeFn({
    mb: 2,
    '.description-title': {
      fontSize: ['18px', '26px'],
      color: 'secondary',
    },

    h3: {
      color: '#999',
      fontSize: ['18px', '26px'],
    },
  })
);

const DescriptionPageSidebar = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    flexDirection: 'column',
    flex: [null, '1'],
    mb: [4, 0],
  })
);

const TileListsWrap = styled.div(
  sx2CssThemeFn({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    flexShrink: [null, '0'],
  })
);

interface IPdpDescriptionProps {
  isRoundtrip?: boolean;
}

const PdpDescription = ({
  isRoundtrip,
}: IPdpDescriptionProps): React.ReactElement => {
  const [t] = useTranslation();
  const { id } = useObjectId();
  const { desktop } = useDeviceType();

  const { data, loading } = useGetPdpDescriptionQuery({
    variables: { id },
  });

  if (loading) {
    return (
      <PdpPageContainer>
        <PdpDescriptionSkeleton />
      </PdpPageContainer>
    );
  }

  return (
    <>
      <PdpStructuredData />
      <PdpPageContainer>
        <DescriptionPageContent>
          <DescriptionPageMain>
            <DescriptionPageTitle>
              <h2 className="description-title">
                {t(`pdp:description.title`)}
              </h2>
              <h3>{data?.pdp.content.overview.productName}</h3>
            </DescriptionPageTitle>
            <ProductDescription
              leadText={desktop ? data?.pdp.content.description.leadText : null}
              blocks={data?.pdp.content.description.textBlocks}
              sellingPropositions={
                data?.pdp.content.description.sellingPropositions
              }
            />
          </DescriptionPageMain>
          <DescriptionPageSidebar>
            {!isRoundtrip ? (
              <DescriptionPageTitle>
                <h2 className="description-title">
                  {t('pdp:description.facts.title')}
                </h2>
              </DescriptionPageTitle>
            ) : null}
            {data?.pdp.content.description.productFeatures.length ? (
              <TileListsWrap>
                <ProductFeatures
                  attributes={
                    data?.pdp.content.description.productFeatures || []
                  }
                />
              </TileListsWrap>
            ) : null}
            {data?.pdp.content.description.includedInPrice && (
              <div>
                <ProductDescription
                  blocks={[data?.pdp.content.description.includedInPrice]}
                />
              </div>
            )}
          </DescriptionPageSidebar>
        </DescriptionPageContent>
      </PdpPageContainer>
    </>
  );
};

export default PdpDescription;
